function debounce(func, wait) {
  let timeout;
  return function() {
    const context = this;
    const args = arguments;
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(context, args), wait);
  };
}

const desktopFacetMq = window.matchMedia('(min-width: 700px)');
const drawerOverlayCache = new WeakMap();
const FILTER_SKIP_KEYS = new Set(['sort_by', 'page', 'section_id', 'q', 'type']);

let lastActiveFilterId = null;
let pendingActiveFilterId = null;
/** Set only when the user clicks a filter group label/button — survives AJAX restore. */
let explicitFilterGroupId = null;
/** Panel to restore after the in-flight facet AJAX completes. */
let facetRestorePanelId = null;
let facetRestoreGeneration = 0;
let facetUpdateInProgress = false;

function patchFacetDrawerHide() {
  if (window.__facetDrawerHidePatched) return;
  const FacetDrawer = customElements.get('facet-drawer');
  if (!FacetDrawer?.prototype?.hide) return;
  const originalHide = FacetDrawer.prototype.hide;
  FacetDrawer.prototype.hide = function() {
    if (this.id === 'facets-drawer' && isDesktopFacetsDrawer() && facetUpdateInProgress) {
      return Promise.resolve();
    }
    return originalHide.call(this);
  };
  window.__facetDrawerHidePatched = true;
}

function isDesktopFacetsDrawer() {
  return desktopFacetMq.matches;
}

function getOpenFacetDrawer() {
  const drawers = document.querySelectorAll('#facets-drawer');
  for (const drawer of drawers) {
    if (drawer.hasAttribute('open')) return drawer;
  }
  return null;
}

function getFacetDrawerElement() {
  const openDrawer = getOpenFacetDrawer();
  if (openDrawer) return openDrawer;
  const drawers = document.querySelectorAll('#facets-drawer');
  for (const drawer of drawers) {
    if (drawer.parentElement === document.body) return drawer;
  }
  return drawers[0] || null;
}

function getDrawerOverlay(drawer) {
  if (!drawer?.shadowRoot) return null;
  let overlay = drawerOverlayCache.get(drawer);
  if (!overlay) {
    overlay = drawer.shadowRoot.querySelector('[part="overlay"]');
    if (overlay) drawerOverlayCache.set(drawer, overlay);
  }
  return overlay;
}

function openFacetDrawer() {
  const drawer = getFacetDrawerElement();
  if (!drawer || drawer.hasAttribute('open')) return drawer;
  if (typeof drawer.show === 'function') {
    drawer.show();
  } else {
    drawer.setAttribute('open', '');
  }
  return drawer;
}

function beginFacetUpdate() {
  if (!isDesktopFacetsDrawer()) return;
  if (!getOpenFacetDrawer()) return;
  facetUpdateInProgress = true;
}

function endFacetUpdate() {
  facetUpdateInProgress = false;
}

function isFacetFilterControl(target) {
  if (!target) return false;
  const input = target.matches('input[type="checkbox"], input[type="radio"]')
    ? target
    : target.closest('input[type="checkbox"], input[type="radio"]');
  if (input?.disabled) return false;
  return (
    target.matches('input[type="checkbox"], input[type="radio"]') ||
    target.matches('label[for]') ||
    !!target.closest('.checkbox-container')
  );
}

function focusFilterGroupPanel(details) {
  if (!details) return;
  details.querySelector('.accordion__content input[type="checkbox"], .accordion__content input[type="radio"]')?.focus({
    preventScroll: true,
  });
}

function captureFacetFilterFocus() {
  const active = document.activeElement;
  if (!active?.closest('#facets-drawer [data-facet-form]')) return null;
  const name = active.getAttribute('name');
  if (!name) return null;
  return { name, value: active.value, type: active.type };
}

function restoreFacetFilterFocus(form, snapshot) {
  if (!form || !snapshot?.name) return;
  const escapedName = CSS.escape(snapshot.name);
  const escapedValue = CSS.escape(snapshot.value);
  const selector =
    snapshot.type === 'radio'
      ? `input[type="radio"][name="${escapedName}"][value="${escapedValue}"]`
      : `input[type="checkbox"][name="${escapedName}"][value="${escapedValue}"]`;
  form.querySelector(selector)?.focus({ preventScroll: true });
}

function getFacetForm() {
  const drawer = getOpenFacetDrawer();
  return drawer?.querySelector('[data-facet-form]') || document.getElementById('facet-form');
}

function syncActiveFilterState(details) {
  if (!details) return;
  lastActiveFilterId = details.id || details.dataset.filterGroup || null;
  window.__facetActiveFilterId = lastActiveFilterId;
  if (window.__activeFilterElements) {
    window.__activeFilterElements.clear();
    if (lastActiveFilterId) window.__activeFilterElements.add(lastActiveFilterId);
  }
}

function setActiveFilter(details) {
  if (!details) return;
  const drawer = details.closest('#facets-drawer, facet-drawer') || getOpenFacetDrawer();
  const scope = drawer || document;
  scope.querySelector('.vt-accordion.active-filter')?.classList.remove('active-filter');
  details.classList.add('active-filter');
  // Desktop drawer panels rely on absolute-positioned content; native <details> toggle
  // must stay open or the browser hides content even when active-filter CSS applies.
  details.setAttribute('open', '');
  details.setAttribute('aria-expanded', 'true');
  syncActiveFilterState(details);
}

function resolveFilterDetails(facetForm, id) {
  if (!id || !facetForm) return null;
  const selectors = [
    `details.vt-accordion[id="${id}"]`,
    `details.vt-accordion[data-filter-group="${id}"]`,
  ];
  if (id === 'sort_by') {
    selectors.push('details.vt-accordion[data-filter-group="sort-by"]');
  }
  for (const selector of selectors) {
    const match = facetForm.querySelector(selector);
    if (match) return match;
  }
  return null;
}

function markUserFilterNavigation(id) {
  if (!id) return;
  explicitFilterGroupId = id;
  pendingActiveFilterId = id;
  facetRestorePanelId = id;
}

function isSortFilterGroup(details) {
  if (!details) return false;
  return details.id === 'sort_by' || details.dataset.filterGroup === 'sort_by';
}

function getFilterGroupId(details) {
  return details?.id || details?.dataset?.filterGroup || null;
}

function clearExplicitFilterNavigationIfDifferent(groupId) {
  if (!groupId || !explicitFilterGroupId || explicitFilterGroupId === groupId) return;
  explicitFilterGroupId = null;
  pendingActiveFilterId = groupId;
}

function getRestorePanelId() {
  return explicitFilterGroupId || pendingActiveFilterId || facetRestorePanelId || lastActiveFilterId;
}

function findFilterToActivate(facetForm) {
  const detailsElements = facetForm.querySelectorAll('details.vt-accordion');
  if (!detailsElements.length) return null;

  for (const id of [pendingActiveFilterId, facetRestorePanelId, lastActiveFilterId]) {
    if (!id) continue;
    const target = resolveFilterDetails(facetForm, id);
    if (target) return target;
  }

  for (const details of detailsElements) {
    if (isSortFilterGroup(details)) continue;
    if (details.querySelector('input[type="checkbox"]:checked')) return details;
  }

  if (lastActiveFilterId === 'sort_by') {
    const sortTarget = resolveFilterDetails(facetForm, 'sort_by');
    if (sortTarget) return sortTarget;
  }

return Array.from(detailsElements).find(details => !isSortFilterGroup(details)) || detailsElements[0];
}

function setupFacets(forceRestore) {
  const facetForm = getFacetForm();
  if (!facetForm) return;

  const detailsElements = facetForm.querySelectorAll('details.vt-accordion');
  if (!detailsElements.length) return;

  detailsElements.forEach(details => {
    details.setAttribute('open', '');
    details.setAttribute('aria-expanded', 'true');
  });

  const preferredTarget = resolveFilterDetails(facetForm, getRestorePanelId());
  const currentActive = facetForm.querySelector('details.vt-accordion.active-filter');

  if (preferredTarget) {
    setActiveFilter(preferredTarget);
    explicitFilterGroupId = null;
    pendingActiveFilterId = null;
  } else if (forceRestore || !currentActive) {
    setActiveFilter(findFilterToActivate(facetForm));
    pendingActiveFilterId = null;
  }
}

function resetApplyButtons(root) {
  root.querySelectorAll('button[is="facet-apply-button"]').forEach(btn => {
    btn.querySelectorAll('[data-facet-filter-count]').forEach(span => span.remove());
    const label = (btn.dataset.facetApplyLabel || btn.textContent || 'VIEW ALL')
      .replace(/\(\d+\)/g, '')
      .trim();
    btn.textContent = label;
    btn.dataset.facetApplyLabel = label;
  });
}

function countActiveFilters(form) {
  let count = 0;
  new FormData(form).forEach((value, key) => {
    if (!value || FILTER_SKIP_KEYS.has(key) || key.startsWith('options[')) return;
    count += 1;
  });
  return count;
}

function countGroupActiveFilters(details) {
  if (!details || isSortFilterGroup(details)) return 0;

  let count = 0;
  details.querySelectorAll('input[type="checkbox"]:checked').forEach(() => {
    count += 1;
  });
  if (count > 0) return count;

  const gte = details.querySelector('input[name="filter.v.price.gte"]');
  const lte = details.querySelector('input[name="filter.v.price.lte"]');
  if (gte?.value || lte?.value) return 1;

  return 0;
}

function updateGroupFilterCounts(form) {
  if (!form) return;
  form.querySelectorAll('details.vt-accordion').forEach(details => {
    const count = countGroupActiveFilters(details);
    const countText = count > 0 ? `(${count})` : '';
    details.querySelectorAll('[data-filter-count]').forEach(span => {
      span.textContent = countText;
    });
  });
}

function updateTopBarFilterCounts(form) {
  if (!form) return;
  document.querySelectorAll('.collection-top-bar .filter-button[data-filter]').forEach(btn => {
    const filterId = btn.getAttribute('data-filter');
    if (!filterId || filterId === 'sort_by' || filterId === 'price') return;

    if (!btn.dataset.filterLabel) {
      btn.dataset.filterLabel = btn.textContent.replace(/\s*\(\d+\)\s*$/, '').trim();
    }

    const count = countGroupActiveFilters(resolveFilterDetails(form, filterId));
    const label = btn.dataset.filterLabel;
    btn.textContent = count > 0 ? `${label} (${count})` : label;
  });
}

function updateApplyButtonCounts(form) {
  if (!form) return;
  const count = countActiveFilters(form);
  form.querySelectorAll('button[is="facet-apply-button"]').forEach(btn => {
    let span = btn.querySelector('[data-facet-filter-count]');
    if (!span) {
      span = document.createElement('span');
      span.setAttribute('data-facet-filter-count', '');
      btn.appendChild(span);
    }
    span.textContent = count > 0 ? `(${count})` : '';
  });
}

/** Instant UI feedback — runs on change; server HTML reconciles on facet:updated. */
function updateFacetCounts(form) {
  form = form || getFacetForm();
  if (!form) return;
  updateApplyButtonCounts(form);
  updateGroupFilterCounts(form);
  updateTopBarFilterCounts(form);
}

function importFacetForm(sourceForm) {
  const clone = sourceForm.cloneNode(true);
  resetApplyButtons(clone);
  return clone;
}

// Open drawer is portaled to <body>; facet:update replaces the in-section copy only.
function syncPortaledFacetDrawer(section) {
  const drawers = document.querySelectorAll('#facets-drawer');
  if (drawers.length < 2) return false;

  let openDrawer = null;
  let sectionDrawer = section?.querySelector('#facets-drawer') || null;

  for (const drawer of drawers) {
    if (drawer.hasAttribute('open')) openDrawer = drawer;
    if (!sectionDrawer && drawer.closest('.shopify-section')) sectionDrawer = drawer;
  }

  if (!openDrawer || !sectionDrawer || openDrawer === sectionDrawer) return false;

  const newForm = sectionDrawer.querySelector('[data-facet-form]');
  const openForm = openDrawer.querySelector('[data-facet-form]');
  if (!newForm || !openForm) return false;

  const focusSnapshot = captureFacetFilterFocus();
  openForm.replaceWith(importFacetForm(newForm));
  restoreFacetFilterFocus(openDrawer.querySelector('[data-facet-form]'), focusSnapshot);
  return true;
}

function restoreFacets(section) {
  syncPortaledFacetDrawer(section);
  setupFacets(true);
  updateFacetCounts(getFacetForm());
  facetRestorePanelId = null;
  requestAnimationFrame(() => endFacetUpdate());
}

function handleFilterButtonClick(event, button) {
  event.preventDefault();
  event.stopPropagation();

  const filterType = button.getAttribute('data-filter');
  markUserFilterNavigation(filterType);

  const drawerOpen = isDesktopFacetsDrawer() && getOpenFacetDrawer();
  if (drawerOpen) {
    beginFacetUpdate();
  } else if (isDesktopFacetsDrawer()) {
    openFacetDrawer();
  }

  const facetForm = getFacetForm();
  const targetDetails = resolveFilterDetails(facetForm, filterType);
  if (!targetDetails) return;

  setActiveFilter(targetDetails);
  if (drawerOpen) {
    requestAnimationFrame(() => {
      focusFilterGroupPanel(targetDetails);
      requestAnimationFrame(() => endFacetUpdate());
    });
  }
}

function handleAccordionSummaryClick(event) {
  event.preventDefault();
  event.stopPropagation();

  const summary = event.target.matches('summary') ? event.target : event.target.closest('summary');
  const details = summary?.parentElement;
  if (!details?.matches('details.vt-accordion')) return;

  const groupId = getFilterGroupId(details);
  markUserFilterNavigation(groupId);

  const drawerOpen = isDesktopFacetsDrawer() && getOpenFacetDrawer();
  if (drawerOpen) beginFacetUpdate();

  setActiveFilter(details);

  if (drawerOpen) {
    requestAnimationFrame(() => {
      const liveDetails = resolveFilterDetails(getFacetForm(), groupId) || details;
      setActiveFilter(liveDetails);
      focusFilterGroupPanel(liveDetails);
      requestAnimationFrame(() => endFacetUpdate());
    });
  }
}

document.addEventListener('click', function(event) {
  const target = event.target;

  if (target.closest('button[is="facet-apply-button"]')) {
    endFacetUpdate();
    return;
  }

  const filterButton = target.matches('.filter-button') ? target : target.closest('.filter-button');
  if (filterButton) {
    handleFilterButtonClick(event, filterButton);
    return;
  }

  if (isDesktopFacetsDrawer() && getOpenFacetDrawer() && target.closest('#facets-drawer [data-facet-form]') && isFacetFilterControl(target)) {
    beginFacetUpdate();
    event.stopPropagation();
  }
}, true);

document.addEventListener('click', function(event) {
  const target = event.target;

  if (target.matches('.more-filters-button') || target.closest('.more-filters-button')) {
    const facetForm = getFacetForm();
    const currentDetailsElements = facetForm?.querySelectorAll('details.vt-accordion') || [];
    currentDetailsElements.forEach(details => {
      details.setAttribute('open', '');
      details.setAttribute('aria-expanded', 'true');
    });
    const hasActive = Array.from(currentDetailsElements).some(d => d.classList.contains('active-filter'));
    if (!hasActive && currentDetailsElements.length > 0) {
      setActiveFilter(currentDetailsElements[currentDetailsElements.length - 1]);
    }
    return;
  }

  if (target.matches('.clear-filters') || target.closest('.clear-filters')) {
    document.querySelectorAll('#facets-drawer .vt-accordion').forEach(d => d.classList.remove('active-filter'));
    lastActiveFilterId = null;
    pendingActiveFilterId = null;
    explicitFilterGroupId = null;
    facetRestorePanelId = null;
    window.__facetActiveFilterId = null;
    window.__activeFilterElements?.clear();
    return;
  }

  if (target.matches('details.vt-accordion summary') || target.closest('details.vt-accordion summary')) {
    if (!target.closest('#facets-drawer [data-facet-form]')) return;
    handleAccordionSummaryClick(event);
    return;
  }
}, false);

// Desktop drawer: filter interactions must not close the drawer (overlay / focus-trap / aria-controls).
document.addEventListener(
  'pointerdown',
  function(event) {
    if (!isDesktopFacetsDrawer()) return;

    const drawer = getOpenFacetDrawer();
    const filterButton = event.target.matches('.filter-button')
      ? event.target
      : event.target.closest('.filter-button');

    if (drawer && filterButton) {
      beginFacetUpdate();
      event.stopPropagation();
      return;
    }

    if (!drawer?.hasAttribute('open')) return;

    const path = event.composedPath();
    const overlay = getDrawerOverlay(drawer);
    if (overlay && path.includes(overlay)) {
      endFacetUpdate();
      return;
    }

    if (!path.includes(drawer)) {
      endFacetUpdate();
      return;
    }

    if (event.target.closest('details.vt-accordion summary')) {
      beginFacetUpdate();
      event.stopPropagation();
      return;
    }

    if (event.target.closest('#facets-drawer [data-facet-form]')) {
      if (isFacetFilterControl(event.target)) {
        beginFacetUpdate();
        const details = event.target.closest('details.vt-accordion');
        clearExplicitFilterNavigationIfDifferent(getFilterGroupId(details));
      }
      event.stopPropagation();
    }
  },
  true
);

document.addEventListener('change', function(event) {
  const input = event.target;
  if (input.type !== 'checkbox' && input.type !== 'radio') return;
  if (input.disabled) return;
  if (!input.closest('[data-facet-form], #facets-drawer')) return;

  const details = input.closest('details.vt-accordion');
  if (!details) return;

  const groupId = getFilterGroupId(details);
  const form = input.closest('[data-facet-form]') || getFacetForm();
  setActiveFilter(details);
  facetRestorePanelId = groupId;
  updateFacetCounts(form);

  // Keep restore targeting the group being edited, but don't let stale change events
  // override an explicit navigation to a different group (e.g. brands → Sort by).
  if (!explicitFilterGroupId || explicitFilterGroupId === groupId) {
    pendingActiveFilterId = groupId;
  }

  beginFacetUpdate();
}, true);

document.addEventListener('facet:update', function() {
  facetRestoreGeneration += 1;
  facetRestorePanelId = lastActiveFilterId || pendingActiveFilterId || explicitFilterGroupId;
  beginFacetUpdate();
});

document.addEventListener('facet:updated', function(event) {
  if (!event.target?.classList?.contains('shopify-section')) return;
  const generation = facetRestoreGeneration;
  requestAnimationFrame(() => {
    if (generation !== facetRestoreGeneration) return;
    restoreFacets(event.target);
  });
});

document.addEventListener('dialog:after-show', function(event) {
  if (event.target.id === 'facets-drawer' || event.target.closest('#facets-drawer')) {
    setupFacets(true);
    updateFacetCounts(getFacetForm());
  }
});

function loadNewContentViaAjax(url) {
  return fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
    .then(response => {
      if (!response.ok) throw new Error('Network response was not ok');
      return response.text();
    })
    .then(html => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newFacetForm = doc.querySelector('[data-facet-form]');
      if (newFacetForm) {
        const container = getFacetForm();
        if (container) {
          resetApplyButtons(newFacetForm);
          container.replaceWith(newFacetForm);
          setupFacets(true);
          updateFacetCounts(getFacetForm());
        }
      }
      history.pushState({}, '', url);
      return newFacetForm;
    })
    .catch(error => console.error('Error loading content:', error));
}

window.addEventListener('popstate', debounce(() => setupFacets(true), 50));

function initFacetSystem() {
  patchFacetDrawerHide();
  if (!window.__activeFilterElements) {
    window.__activeFilterElements = new Set();
  }
  setupFacets(true);
  updateFacetCounts(getFacetForm());
}

if ('requestIdleCallback' in window) {
  window.addEventListener('DOMContentLoaded', () => {
    requestIdleCallback(initFacetSystem);
  });
} else {
  window.addEventListener('DOMContentLoaded', () => {
    setTimeout(initFacetSystem, 0);
  });
}

window.facetSystem = {
  setup: setupFacets,
  loadContent: loadNewContentViaAjax,
  refresh: debounce(() => setupFacets(true), 50),
  syncPortaledFacetDrawer,
  updateCounts: updateFacetCounts,
};
